import { Api } from '../models/Api';
import { BehaviorSubject } from 'rxjs';
import ErrorService from './ErrorService';
import { AxiosError } from 'axios';
import ErrorBoundaryService, { ErrorType } from './ErrorBoundaryService';
import SpinnerService from './SpinnerService';

export type ApiState = {
  api: Api<unknown>;
  initialized: boolean;
  locale: string | undefined;
  language: string | undefined;
  country: string | undefined;
};

const initialState: ApiState = {
  api: new Api({
    baseURL: process.env.REACT_APP_API_BASE_HOST,
  }),
  initialized: false,
  locale: undefined,
  language: undefined,
  country: undefined,
};

const state$ = new BehaviorSubject<ApiState>(initialState);

const getState = () => {
  return state$.getValue();
};

const setState = (data: ApiState) => {
  return state$.next({ ...data });
};

const handleApiServiceError = () => {
  ErrorBoundaryService.setErrorBoundaryState(ErrorType.ApiServiceError);
};

const handleApiError = (error: AxiosError) => {
  if (error?.response?.status === 401) {
    const encodedUri = encodeURI(process.env.REACT_APP_LEGACY_DASHBOARD_REDIRECT_URL + window.location.href);
    window.location.replace(`${process.env.REACT_APP_LOGIN_REDIRECT_URL}${encodedUri}`);
    return;
  }

  if (error?.response?.status === 404 && error.response.config.method === 'get' && error.response.config.url?.indexOf('/api/Systems/') === 0) {
    ErrorBoundaryService.setErrorBoundaryState(ErrorType.SystemNotFound);
    return;
  }

  ErrorService.addError(error);
};

const isPollingUrl = (url: string | undefined) => {
  return url === '/api/Sizing/ProductCacheStatus' || url?.startsWith('/api/Sizing/FilterSession/');
};

const ApiService = {
  init: (token: string, locale: string) => {
    const state = getState();
    const apiHttpClient = state.api;
    apiHttpClient.instance.defaults.headers.common.Authorization = `Bearer ${token}`;
    apiHttpClient.instance.defaults.headers.common['Accept-Language'] = locale;
    apiHttpClient.instance.interceptors.request.use((request) => {
      if (!isPollingUrl(request.url))
        //show spinner (add a new request count)
        SpinnerService.updateSpinnerStatus(1, true);
      return request;
    });
    apiHttpClient.instance.interceptors.response.use(
      (response) => {
        if (!isPollingUrl(response.config.url)) {
          //decrement the resolved request count
          SpinnerService.updateSpinnerStatus(-1, true);
        }
        if (response.status === 401) {
          const encodedUri = encodeURI(process.env.REACT_APP_LEGACY_DASHBOARD_REDIRECT_URL + window.location.href);
          window.location.replace(`${process.env.REACT_APP_LOGIN_REDIRECT_URL}${encodedUri}`);
        }
        return response;
      },
      (error) => {
        if (!isPollingUrl(error?.response?.config?.url))
          //decrement the resolved request count
          SpinnerService.updateSpinnerStatus(-1, true);
        console.error('API service got a response error', error.response, error.request);
        handleApiError(error);
        if (!error.response) {
          handleApiServiceError();
        }
        return Promise.reject(error);
      },
    );
    state.initialized = true;
    state.locale = locale;
    const localeParts = locale.split('-');
    state.language = localeParts[0];
    state.country = localeParts.length > 1 ? locale.split('-')[1] : 'US';
    setState(state);
  },

  getApi: () => {
    if (!state$.getValue().initialized) {
      throw new Error('API Not Initialized');
    }
    return state$.getValue().api.api;
  },

  getState: () => {
    return state$.value;
  },
};

export default ApiService;
